import axios from 'axios';

// Base URL for backend API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add authorization token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Candle data response from backend
export interface CandleResponse {
  time: string;      // Format: yyyy-MM-dd
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// Types for Screener APIs
export interface ScreenerMasterResponse {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: string;
  isActive: boolean;
  isPremium: boolean;
  sortOrder: number;
  chartView: string; // 'CLASSIC' | 'DRAWING'
}

export interface ChartDataResponse {
  symbol: string;
  exchange: string;
  name: string;
  timeframe: string;
  totalCandles: number;
  candles: CandleResponse[];
}

export interface ChartDrawingsResponse {
  stockId: number;
  stockSymbol: string;
  hasDrawings: boolean;
  drawings: any[]; // deserialized drawing objects
}

export interface ScreenerStockResponse {
  id: number;
  symbol: string;
  stockName: string;
  exchange: string;
  capturedPrice: number;
  currentPrice: number | null;
  capturedOn: string;
  maxUpside: number | null;
  maxUpsidePrice: number | null;
  maxUpsideDate: string | null;
  currentUpside: number | null;
  resistanceLevel: number | null;
  supportLevel: number | null;
  sector: string | null;
  industry: string | null;
  marketCap: number | null;
  volume: number | null;
  timeframe: string;
  chartUrl: string | null;
  stockRank: number;
  notes: string | null;
  eliminated: boolean | null;  // Whether stock signal is closed (close < 45-day SMA)
  hiddenForFutureUsers: boolean | null;  // If true, only users subscribed before capturedOn can see it
  candles: CandleResponse[] | null;  // Chart candle data from backend
}

export type ScreenerSortField = 'maxUpside';
export type ScreenerSortDir = 'asc' | 'desc';

export interface ScreenerStockFilters {
  symbols?: string[];
  /** Inclusive lower bound for capturedOn date, format yyyy-MM-dd */
  capturedFrom?: string;
  /** Inclusive upper bound for capturedOn date, format yyyy-MM-dd */
  capturedTo?: string;
  /** Minimum day-over-day % change of the latest candle (e.g. 5 = +5% or more) */
  minLatestChangePct?: number;
  sortBy?: ScreenerSortField;
  sortDir?: ScreenerSortDir;
}

export interface PageResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface ApiResponse<T> {
  data: T;
  statusCode: string;
  message: string;
}

/**
 * Screener API Service
 */
const screenerApi = {
  /**
   * Get stocks for a screener (ordered by capturedOn DESC unless overridden by filters)
   * @param screenerCode - Screener code (e.g., 'MOMENTUM_AI')
   * @param page - Page number (1-indexed)
   * @param pageSize - Number of items per page
   * @param filters - Optional symbol filter and sort options
   */
  getStocks: async (
    screenerCode: string,
    page: number = 1,
    pageSize: number = 20,
    filters?: ScreenerStockFilters
  ): Promise<ApiResponse<PageResponse<ScreenerStockResponse>>> => {
    const params: Record<string, string | number> = { page, pageSize };
    if (filters?.symbols && filters.symbols.length > 0) {
      params.symbols = filters.symbols.join(',');
    }
    if (filters?.capturedFrom) {
      params.capturedFrom = filters.capturedFrom;
    }
    if (filters?.capturedTo) {
      params.capturedTo = filters.capturedTo;
    }
    if (filters?.minLatestChangePct !== undefined) {
      params.minLatestChangePct = filters.minLatestChangePct;
    }
    if (filters?.sortBy) {
      params.sortBy = filters.sortBy;
      params.sortDir = filters.sortDir ?? 'desc';
    }
    const response = await apiClient.get(
      `/screeners/${screenerCode}/stocks`,
      { params }
    );
    return response.data;
  },

  /**
   * Get candle data for a stock symbol (1D timeframe)
   */
  getStockCandles: async (symbol: string): Promise<ApiResponse<ChartDataResponse>> => {
    const response = await apiClient.get(`/charts/${symbol}/candles`, { params: { timeframe: '1D' } });
    return response.data;
  },

  /**
   * Get all active screeners
   */
  getScreeners: async (): Promise<ApiResponse<ScreenerMasterResponse[]>> => {
    const response = await apiClient.get('/screeners');
    return response.data;
  },

  /**
   * Get a single screener by code
   */
  getScreenerByCode: async (code: string): Promise<ApiResponse<ScreenerMasterResponse>> => {
    const response = await apiClient.get(`/screeners/${code}`);
    return response.data;
  },

  /**
   * Get all stocks for a screener (admin view — all statuses)
   */
  getAdminStocks: async (
    screenerCode: string,
    page: number = 1,
    pageSize: number = 100
  ): Promise<ApiResponse<PageResponse<ScreenerStockResponse>>> => {
    const response = await apiClient.get(`/admin/screener-stocks/${screenerCode}`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  /**
   * Get drawings for a stock (admin)
   */
  getDrawings: async (stockId: number): Promise<ApiResponse<ChartDrawingsResponse>> => {
    const response = await apiClient.get(`/admin/screener-stocks/${stockId}/drawings`);
    return response.data;
  },

  /**
   * Save drawings for a stock (admin)
   */
  saveDrawings: async (stockId: number, drawings: any[]): Promise<ApiResponse<ChartDrawingsResponse>> => {
    const response = await apiClient.post(`/admin/screener-stocks/${stockId}/drawings`, {
      drawings: JSON.stringify(drawings),
    });
    return response.data;
  },

  /**
   * Toggle chart view for a screener between CLASSIC and DRAWING (admin only)
   */
  toggleChartView: async (screenerCode: string): Promise<ApiResponse<ScreenerMasterResponse>> => {
    const response = await apiClient.patch(`/admin/screeners/${screenerCode}/chart-view`);
    return response.data;
  },

  /**
   * Get drawings for a stock (user-facing, requires screener access)
   */
  getStockDrawings: async (screenerCode: string, stockId: number): Promise<ApiResponse<ChartDrawingsResponse>> => {
    const response = await apiClient.get(`/screeners/${screenerCode}/stocks/${stockId}/drawings`);
    return response.data;
  },

  /**
   * Toggle hidden_for_future_users flag for a stock (admin only)
   */
  toggleVisibility: async (stockId: number): Promise<ApiResponse<ScreenerStockResponse>> => {
    const response = await apiClient.patch(`/admin/screener-stocks/${stockId}/visibility`);
    return response.data;
  },

  /**
   * Get stocks history for backtest (ordered by capturedOn DESC)
   * @param screenerCode - Screener code (e.g., 'MOMENTUM_AI')
   * @param page - Page number (1-indexed)
   * @param pageSize - Number of items per page
   */
  getHistory: async (
    screenerCode: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<ApiResponse<PageResponse<ScreenerStockResponse>>> => {
    const response = await apiClient.get(
      `/screeners/${screenerCode}/history`,
      {
        params: { page, pageSize },
      }
    );
    return response.data;
  },

  getUnreadCounts: async (): Promise<Record<string, number>> => {
    const response = await apiClient.get('/screeners/unread-counts');
    return response.data.data;
  },

  markScreenerSeen: async (screenerCode: string): Promise<void> => {
    await apiClient.post(`/screeners/${screenerCode}/mark-seen`);
  },
};

export default screenerApi;

/**
 * Legacy function for backward compatibility with old ScreenerTable component
 * This generates dummy data - used by components that haven't been migrated yet
 */
export const fetchScreenerData = async (
  endpoint: string,
  queryString: string
): Promise<{
  data: any[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}> => {
  // Parse query parameters
  const params = new URLSearchParams(queryString);
  const page = parseInt(params.get('page') || '1', 10);
  const pageSize = parseInt(params.get('pageSize') || '10', 10);

  // Generate dummy data for old components
  const dummyData = generateDummyData(100);

  // Calculate pagination
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = dummyData.slice(startIndex, endIndex);

  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  return {
    data: paginatedData,
    pagination: {
      currentPage: page,
      totalPages: Math.ceil(dummyData.length / pageSize),
      totalItems: dummyData.length,
      pageSize: pageSize,
    },
  };
};

// Dummy data generator for legacy components
const generateDummyData = (count: number): any[] => {
  const stocks = [
    'Reliance Industries Ltd.',
    'Tata Consultancy Services Ltd.',
    'HDFC Bank Ltd.',
    'Infosys Ltd.',
    'ICICI Bank Ltd.',
  ];

  const symbols = ['RELIANCE', 'TCS', 'HDFCBANK', 'INFY', 'ICICIBANK'];
  const exchanges = ['NSE', 'BSE'];
  const timeframes = ['Daily', 'Weekly', 'Monthly'];

  const data: any[] = [];

  for (let i = 0; i < count; i++) {
    const stockIndex = i % stocks.length;
    const capturedDate = new Date(2024, 11, Math.floor(Math.random() * 20) + 1);
    const capturedPrice = (Math.random() * 2000 + 500).toFixed(2);

    data.push({
      id: `stock-${i + 1}`,
      stockName: stocks[stockIndex],
      symbol: symbols[stockIndex],
      exchange: exchanges[Math.floor(Math.random() * exchanges.length)],
      timeframe: timeframes[Math.floor(Math.random() * timeframes.length)],
      capturedPrice: `₹${capturedPrice}`,
      capturedOn: capturedDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
      chartUrl: `https://in.tradingview.com/chart/${symbols[stockIndex]}/`,
      maxMove: Number((Math.random() * 55 - 5).toFixed(2)),
      chartConfig: {
        capturedPriceLine: true,
      },
    });
  }

  return data;
};
